import type { ClientPositionView, CreatePositionInput, YaasPositionStatus } from "@solomon-apps/shared";
import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    createClientPosition,
    fetchAllClientDistributions,
    fetchClientDistributions,
    fetchClientEarnings,
    fetchClientPositions,
    resubmitPosition
} from "@/services/clientPositions";
import type { ClientEarningsResponse, DistributionItem } from "@/services/clientPositions";
import { editPositionDestinations, type EditPositionDestinationsInput } from "@/services/clientRewardsDestination";
import {
    registerClientWallet,
    type RegisterWalletInput,
    type WalletRegistration
} from "@/services/clientWalletEnrollment";

import { useClientContextKey } from "./useClientContextKey";

const POSITION_QUERY_KEYS = [["client-positions"], ["client-earnings"], ["client-distributions"]] as const;
const PROGRAM_POSITION_QUERY_KEYS = [
    ["client-program-surfaces"],
    ["yaas-client", "programs"],
    ["program-policies"]
] as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

interface UseClientPositionsReturn {
    positions: ClientPositionView[];
    isLoading: boolean;
    isPending: boolean;
    refetch: () => Promise<unknown>;
    error: Error | null;
}

export function useClientPositions(status?: YaasPositionStatus, rewardHistory?: "all"): UseClientPositionsReturn {
    const contextKey = useClientContextKey();
    const { data, isLoading, isPending, refetch, error } = useQuery<ClientPositionView[], Error>({
        queryKey: ["client-positions", contextKey, status ?? "all", ...(rewardHistory ? [rewardHistory] : [])],
        queryFn: () => fetchClientPositions(status, rewardHistory),
        staleTime: 15 * 1000,
        // Enrollment reconciliation may finish after the POST receipt. Refresh
        // even an Address-only inventory so existing wallets acquire new LPs.
        refetchInterval: 15 * 1000,
        refetchOnWindowFocus: "always"
    });

    return {
        positions: data ?? [],
        isLoading,
        isPending,
        refetch,
        error
    };
}

// ---------------------------------------------------------------------------
// Mutations — return the raw `useMutation` result. Callers use the standard
// react-query surface: `mutate`, `mutateAsync`, `isPending`, `error`.
// ---------------------------------------------------------------------------

export function useRegisterWallet(): UseMutationResult<WalletRegistration, Error, RegisterWalletInput> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: registerClientWallet,
        onSuccess: async () => {
            await Promise.all(
                [...POSITION_QUERY_KEYS, ...PROGRAM_POSITION_QUERY_KEYS, ["client-wallets"]].map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                )
            );
        }
    });
}

export function useCreatePosition(): UseMutationResult<ClientPositionView, Error, CreatePositionInput> {
    const queryClient = useQueryClient();
    return useMutation<ClientPositionView, Error, CreatePositionInput>({
        mutationFn: createClientPosition,
        onSuccess: async () => {
            await Promise.all(
                [...POSITION_QUERY_KEYS, ...PROGRAM_POSITION_QUERY_KEYS].map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                )
            );
        }
    });
}

export function useEditPositionDestinations(): UseMutationResult<
    ClientPositionView,
    Error,
    { positionUuid: string; data: EditPositionDestinationsInput }
> {
    const queryClient = useQueryClient();
    return useMutation<ClientPositionView, Error, { positionUuid: string; data: EditPositionDestinationsInput }>({
        mutationFn: ({ positionUuid, data }) => editPositionDestinations(positionUuid, data),
        onSuccess: async () => {
            await Promise.all(
                [...POSITION_QUERY_KEYS, ["client-wallets"]].map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                )
            );
        }
    });
}

export function useResubmitPosition(): UseMutationResult<ClientPositionView, Error, string> {
    const queryClient = useQueryClient();
    return useMutation<ClientPositionView, Error, string>({
        mutationFn: resubmitPosition,
        onSuccess: async () => {
            await Promise.all(POSITION_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
        }
    });
}

// ---------------------------------------------------------------------------
// Earnings
// ---------------------------------------------------------------------------

interface UseClientEarningsReturn {
    data: ClientEarningsResponse | null;
    isLoading: boolean;
    /** No result yet, including a retry React Query has paused (hidden tab,
     *  offline), which `isLoading` reports as false. */
    isPending: boolean;
    error: Error | null;
    refetch: () => Promise<unknown>;
}

export function useClientEarnings(): UseClientEarningsReturn {
    const contextKey = useClientContextKey();
    const { data, isLoading, isPending, error, refetch } = useQuery<ClientEarningsResponse, Error>({
        queryKey: ["client-earnings", contextKey],
        queryFn: fetchClientEarnings,
        staleTime: 2 * 60 * 1000
    });

    return { data: data ?? null, isLoading, isPending, error, refetch };
}

// ---------------------------------------------------------------------------
// Distributions
// ---------------------------------------------------------------------------

interface UseClientDistributionsReturn {
    distributions: DistributionItem[];
    isLoading: boolean;
    error: Error | null;
}

export function useClientDistributions(params?: {
    limit?: number;
    page?: number;
    sortOrder?: "ASC" | "DESC";
}): UseClientDistributionsReturn {
    const contextKey = useClientContextKey();
    const { data, isLoading, error } = useQuery({
        queryKey: ["client-distributions", contextKey, params],
        queryFn: () => fetchClientDistributions(params),
        staleTime: 2 * 60 * 1000
    });

    return {
        distributions: data?.data ?? [],
        isLoading,
        error
    };
}

interface UseAllClientDistributionsReturn {
    distributions: DistributionItem[];
    /** No result yet, including a paused retry (see `useClientEarnings`). */
    isPending: boolean;
    error: Error | null;
    refetch: () => Promise<unknown>;
}

/** Every payout, oldest first (all pages). Shared by Reporting and position detail. */
export function useAllClientDistributions(): UseAllClientDistributionsReturn {
    const contextKey = useClientContextKey();
    const { data, isPending, error, refetch } = useQuery<DistributionItem[], Error>({
        queryKey: ["client-distributions", contextKey, "all"],
        queryFn: fetchAllClientDistributions,
        staleTime: 2 * 60 * 1000
    });

    return { distributions: data ?? [], isPending, error, refetch };
}
